import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ethers } from 'ethers';
import { SERVER_NAME } from '../config';

// Create the context
export const AuthContext = createContext();

// Provider component that wraps your app and makes auth object available to any
// child component that calls useAuth()
export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    isAuthenticated: false,
    authChecked: false,
    username: '',
    userID: '',
    userAddress: '',
    roles: [],
    // Wallet connection state
    isWalletConnected: false,
    ethersProvider: null,
    // Added price-related state
    tokenPrice: 0,
    tokenUniswapVersion: 0,
    e3dPrice: 0,
    rate: null,
    // User preferences
    subscribeNewsletter: false
  });

  // Initialize from localStorage on mount
  useEffect(() => {
    const storedIsAuth = localStorage.getItem('isAuthenticated');
    const storedUsername = localStorage.getItem('username');
    const storedUserID = localStorage.getItem('userID');
    const storedUserAddress = localStorage.getItem('userAddress');
    const walletDisconnected = localStorage.getItem('walletDisconnected') === 'true';
    let storedRoles = [];
    try {
      const raw = localStorage.getItem('roles');
      storedRoles = raw ? JSON.parse(raw) : [];
    } catch (e) {
      storedRoles = [];
    }
    const storedNewsletterSub = localStorage.getItem('subscribeNewsletter');
    
    console.log('AuthContext: Initializing from localStorage', { 
      storedIsAuth, storedUsername, storedUserID, storedNewsletterSub 
    });
    
    if (storedIsAuth === 'true' && (storedUsername || storedUserID)) {
      // Convert the stored newsletter subscription value to a boolean
      const newsletterStatus = storedNewsletterSub === 'true' || storedNewsletterSub === true;
      console.log('Setting newsletter subscription to:', newsletterStatus);
      
      setAuth(prevState => ({
        ...prevState,
        isAuthenticated: true,
        authChecked: false,
        username: storedUsername || '',
        userID: storedUserID || '',
        userAddress: storedUserAddress || '',
        roles: Array.isArray(storedRoles) ? storedRoles : [],
        subscribeNewsletter: newsletterStatus
      }));
    } else {
      setAuth(prevState => ({
        ...prevState,
        authChecked: true,
      }));
    }
    
    // Check if wallet was connected and can be reconnected
    if (!walletDisconnected && storedUserAddress && window.ethereum) {
      checkWalletConnection();
    }
  }, []);
  
  // Check if wallet is connected
  const checkWalletConnection = async () => {
    // First check localStorage for stored wallet address
    const storedAddress = localStorage.getItem('userAddress');
    const walletDisconnected = localStorage.getItem('walletDisconnected') === 'true';
    if (walletDisconnected) {
      return false;
    }
    
    if (window.ethereum) {
      try {
        // Check if we have access without prompting
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          const provider = new ethers.providers.Web3Provider(window.ethereum);
          setAuth(prev => ({
            ...prev,
            userAddress: accounts[0],
            isWalletConnected: true,
            ethersProvider: provider
          }));
          localStorage.setItem('userAddress', accounts[0]);
          localStorage.setItem('walletConnected', 'true');
          console.log('Wallet connected and stored in context:', accounts[0]);
          return true;
        } else if (storedAddress) {
          // If we have a stored address but no active accounts, try to reconnect silently
          console.log('Found stored wallet address, attempting to restore connection:', storedAddress);
          const provider = new ethers.providers.Web3Provider(window.ethereum);
          setAuth(prev => ({
            ...prev,
            userAddress: storedAddress,
            isWalletConnected: true,
            ethersProvider: provider
          }));
          return true;
        }
      } catch (error) {
        console.error('Error checking wallet connection:', error);
        // Even if there's an error, use the stored address if available
        if (storedAddress) {
          console.log('Restoring connection from localStorage despite error');
          setAuth(prev => ({
            ...prev,
            userAddress: storedAddress,
            isWalletConnected: true
          }));
          return true;
        }
      }
    } else if (storedAddress) {
      // If Ethereum is not available but we have a stored address, still update the UI state
      console.log('Ethereum not available, but restoring wallet address from localStorage');
      setAuth(prev => ({
        ...prev,
        userAddress: storedAddress,
        isWalletConnected: true
      }));
      return true;
    }
    return false;
  };

  // Helper functions to update auth state
  const login = (userData) => {
    const { username, userID, userAddress, subscribeNewsletter, roles } = userData;
    console.log('AuthContext login with data:', userData);
    
    setAuth(prev => ({
      ...prev,
      isAuthenticated: true,
      authChecked: true,
      username: username || '',
      userID: userID || '',
      userAddress: userAddress || '',
      roles: Array.isArray(roles) ? roles : [],
      subscribeNewsletter: subscribeNewsletter === true || subscribeNewsletter === 'true'
    }));
    
    // Update localStorage
    localStorage.setItem('isAuthenticated', 'true');
    
    if (username) localStorage.setItem('username', username);
    if (userID) localStorage.setItem('userID', userID);
    if (userAddress) localStorage.setItem('userAddress', userAddress);
    try { localStorage.setItem('roles', JSON.stringify(Array.isArray(roles) ? roles : [])); } catch (e) {}
    if (subscribeNewsletter !== undefined) localStorage.setItem('subscribeNewsletter', subscribeNewsletter);
  };

  const logout = useCallback(async () => {
    console.log('AuthContext: Logging out');
    
    try {
      // Attempt to clear the server-side session so /auth/status won't re-authenticate
      await axios.get(`${SERVER_NAME}/logout`, { withCredentials: true });
    } catch (e) {
      console.warn('AuthContext: Server logout request failed (continuing with client logout):', e?.message || e);
    }

    // Clear localStorage
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
    localStorage.removeItem('userID');
    localStorage.removeItem('userAddress');
    localStorage.removeItem('subscribeNewsletter');
    localStorage.removeItem('walletConnected');
    localStorage.removeItem('roles');

    // Reset context state
    setAuth({
      isAuthenticated: false,
      authChecked: true,
      username: '',
      userID: '',
      userAddress: '',
      roles: [],
      isWalletConnected: false,
      ethersProvider: null,
      subscribeNewsletter: false,
      tokenPrice: 0,
      tokenUniswapVersion: 0,
      e3dPrice: 0,
      rate: null
    });
  }, []);

  // Verify that the backend still considers the user authenticated.
  // If not, force a local logout so the UI doesn't get stuck in a stale "logged in" state.
  useEffect(() => {
    if (!auth.isAuthenticated) return;

    let running = true;

    const checkBackendAuth = async () => {
      if (!running) return;
      try {
        const resp = await axios.get(`${SERVER_NAME}/auth/status`, { withCredentials: true });
        const ok = !!(resp?.data?.authenticated ?? resp?.data?.isAuthenticated);
        if (!ok) {
          await logout();
          return;
        }

        const nextRoles = resp && resp.data && resp.data.user && Array.isArray(resp.data.user.roles) ? resp.data.user.roles : [];
        setAuth((prev) => {
          if (!prev.isAuthenticated) return prev;
          const same = Array.isArray(prev.roles) && Array.isArray(nextRoles)
            && prev.roles.length === nextRoles.length
            && prev.roles.every((r, i) => r === nextRoles[i]);
          const nextChecked = true;
          if (same && prev.authChecked === nextChecked) return prev;
          try { localStorage.setItem('roles', JSON.stringify(nextRoles)); } catch (e) {}
          return { ...prev, roles: nextRoles, authChecked: nextChecked };
        });
      } catch (e) {
        const status = e && e.response ? e.response.status : null;
        if (status === 401 || status === 403) {
          await logout();
          return;
        }

        setAuth((prev) => (prev.authChecked ? prev : { ...prev, authChecked: true }));
      }
    };

    // Check immediately, then periodically.
    checkBackendAuth();
    const interval = setInterval(checkBackendAuth, 60000);

    // Also check when the tab becomes active again.
    const onFocus = () => {
      checkBackendAuth();
    };
    window.addEventListener('focus', onFocus);

    return () => {
      running = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [auth.isAuthenticated, logout]);

  // Update user information without full login/logout
  const updateUserInfo = (userData) => {
    const { username, userID, userAddress, roles } = userData;
    
    console.log('AuthContext: Updating user info', userData);
    
    // Only update fields that are provided
    const updatedAuth = { ...auth };
    
    if (username !== undefined) {
      updatedAuth.username = username;
      localStorage.setItem('username', username);
    }
    
    if (userID !== undefined) {
      updatedAuth.userID = userID;
      localStorage.setItem('userID', userID);
    }
    
    if (userAddress !== undefined) {
      updatedAuth.userAddress = userAddress;
      
      // If userAddress is being updated to a valid address, update wallet connection state
      if (userAddress) {
        localStorage.setItem('userAddress', userAddress);
        localStorage.setItem('walletConnected', 'true');
        localStorage.removeItem('walletDisconnected');
        updatedAuth.isWalletConnected = true;
        if (window.ethereum) {
          updatedAuth.ethersProvider = new ethers.providers.Web3Provider(window.ethereum);
        }
      } else {
        // If userAddress is null or empty, wallet is disconnected
        localStorage.removeItem('userAddress');
        localStorage.removeItem('walletConnected');
        updatedAuth.isWalletConnected = false;
        updatedAuth.ethersProvider = null;
      }
    }

    if (roles !== undefined) {
      const nextRoles = Array.isArray(roles) ? roles : [];
      updatedAuth.roles = nextRoles;
      try { localStorage.setItem('roles', JSON.stringify(nextRoles)); } catch (e) {}
    }
    
    setAuth(updatedAuth);
  };

  // Update price-related state
  const updatePrices = (priceData) => {
    const { tokenPrice, tokenUniswapVersion, e3dPrice, rate } = priceData;
    
    console.log('AuthContext: Updating prices', priceData);
    
    // IMPORTANT: Only update price-related fields without modifying authentication state
    setAuth(prevAuth => {
      // Create a new state object from previous state
      const updatedAuth = { ...prevAuth };
      
      // Only update price fields that are provided
      if (tokenPrice !== undefined) updatedAuth.tokenPrice = tokenPrice;
      if (tokenUniswapVersion !== undefined) updatedAuth.tokenUniswapVersion = tokenUniswapVersion;
      if (e3dPrice !== undefined) updatedAuth.e3dPrice = e3dPrice;
      if (rate !== undefined) updatedAuth.rate = rate;
      
      return updatedAuth;
    });
  };
  
  // Individual setter functions for convenience
  const setTokenPrice = (price) => updatePrices({ tokenPrice: price });
  const setTokenUniswapVersion = (version) => updatePrices({ tokenUniswapVersion: version });
  const setE3dPrice = (price) => updatePrices({ e3dPrice: price });
  const setRate = (rate) => updatePrices({ rate });
  
  // Update newsletter subscription status
  const updateNewsletterSubscription = (status) => {
    console.log('Updating newsletter subscription status:', status);
    
    // Update state
    setAuth(prev => ({
      ...prev,
      subscribeNewsletter: status
    }));
    
    // Update localStorage
    localStorage.setItem('subscribeNewsletter', status);
  };
  
  // Connect wallet function
  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        console.log('Connecting to MetaMask from AuthContext');
        
        // Force MetaMask dialog to appear by requesting permissions first
        await window.ethereum.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }]
        });
        
        // Then get the selected account
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        const account = accounts[0];
        
        console.log('Connected to MetaMask with account:', account);
        
        // Create provider
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        
        // Update state with wallet info
        setAuth(prev => ({
          ...prev,
          userAddress: account,
          isWalletConnected: true,
          ethersProvider: provider
        }));
        
        // Update localStorage
        localStorage.setItem('userAddress', account);
        localStorage.setItem('walletConnected', 'true');
        localStorage.removeItem('walletDisconnected');
        
        return account;
      } catch (error) {
        console.error('Error connecting to MetaMask:', error);
        throw error;
      }
    } else {
      console.error('MetaMask not detected');
      throw new Error('Please install MetaMask to connect your wallet');
    }
  };
  
  // Disconnect wallet function
  const disconnectWallet = () => {
    console.log('Disconnecting wallet from AuthContext');
    
    // Update state
    setAuth(prev => ({
      ...prev,
      userAddress: '',
      isWalletConnected: false,
      ethersProvider: null
    }));
    
    // Update localStorage
    localStorage.removeItem('userAddress');
    localStorage.removeItem('walletConnected');
    localStorage.setItem('walletDisconnected', 'true');
  };

  // Fetch ETH price from server
  const fetchRate = async () => {
    try {
      // Only fetch rates if authenticated to avoid unnecessary API calls
      if (!auth.isAuthenticated) {
        console.log('Skipping rate fetch - user not authenticated');
        return;
      }
      
      const response = await axios.get(`${SERVER_NAME}/rate`);
      if (response.data && response.data.rate) {
        setRate(response.data.rate);
      }
    } catch (error) {
      console.error('Error fetching ETH rate:', error);
      // Don't logout on API errors
    }
  };

  // Fetch price data on component mount and at intervals
  useEffect(() => {
    // Initial fetch
    fetchRate();
    
    // Set up interval to refresh rates (every 60 seconds)
    const interval = setInterval(() => {
      fetchRate();
    }, 60000);
    
    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, []);

  return (
    <AuthContext.Provider value={{ 
      ...auth, 
      login, 
      logout,
      updateUserInfo,
      // Wallet connection functions
      connectWallet,
      disconnectWallet,
      checkWalletConnection,
      // Expose price-related functions
      updatePrices,
      setTokenPrice,
      setTokenUniswapVersion,
      setE3dPrice,
      setRate,
      fetchRate,
      // User preferences
      updateNewsletterSubscription
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
